import {
  Box,
  Display,
  DisplayPosition,
  JustifyContent,
  ZIndex,
} from '@gousto-internal/citrus-react'
import React, { useEffect, useRef, useState } from 'react'
import { Background } from './components/Background/Background'
import { Carousel } from './components/Carousel/Carousel'
import { CTA } from './components/CTA/CTA'
import { GetStartedStickyCTA } from './components/GetStartedStickyCTA/GetStartedStickyCTA'
import { canUseWindow } from '@library/environment'
import { HomepageNDDBanner, useDeliveryEligibility } from '@library/delivery-timing'
import { trackNDDBannerImpression, trackNDDCTAClick } from './tracking'
import { DiscountBanner } from './components/DiscountBanner/DiscountBanner'

export type LandingPageAboveTheFoldProps = {
  onCTAClick: () => void
  signupCTA: string
}

export function LandingPageAboveTheFold({ onCTAClick, signupCTA }: LandingPageAboveTheFoldProps) {
  const heroRef = useRef<HTMLDivElement>(null)
  const hasTrackedBanner = useRef(false)
  const [isHeroInView, setIsHeroInView] = useState(true)
  const {
    isEnabled: isDeliveryEligibilityEnabled,
    bannerConfig,
    eligibility,
  } = useDeliveryEligibility('homepage')

  useEffect(() => {
    if (!canUseWindow) return

    const observer = new IntersectionObserver(([entry]) => setIsHeroInView(entry.isIntersecting), {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    })

    if (heroRef.current) {
      observer.observe(heroRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  // Track NDD Banner impression once when banner is shown
  if (isDeliveryEligibilityEnabled && eligibility && !hasTrackedBanner.current) {
    trackNDDBannerImpression(eligibility)
    hasTrackedBanner.current = true
  }

  const handleNDDCTAClick = () => {
    trackNDDCTAClick(eligibility)
    onCTAClick()
  }

  return (
    <Box data-testid="landing-page-above-the-fold" ref={heroRef}>
      <Box
        position={DisplayPosition.Relative}
        display={Display.Flex}
        flexBasis={'100%'}
        width={'100%'}
        height={['580px', '610px']}
      >
        <Box
          width="100%"
          position={DisplayPosition.Absolute}
          zIndex={ZIndex.Mask}
          bottom={['-8px', '16px', '-8px', '45px']}
        >
          <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
            <DiscountBanner onBannerCTAClick={onCTAClick} />
          </Box>
          <CTA onCTAClick={onCTAClick} signupCTA={signupCTA} />
        </Box>

        <Box
          minHeight={'610px'}
          width="100%"
          position={DisplayPosition.Absolute}
          zIndex={ZIndex.Masked}
        >
          <Carousel />
        </Box>
        <Box
          minHeight={'610px'}
          width="100%"
          display={Display.Flex}
          position={DisplayPosition.Absolute}
          zIndex={ZIndex.Default}
        >
          <Background />
        </Box>
      </Box>

      {!isHeroInView && <GetStartedStickyCTA signupCTA={signupCTA} onCTAClick={onCTAClick} />}

      {isDeliveryEligibilityEnabled ? (
        <>
          <Box height={['30px', '0px']} /> {/* Mobile-only spacer */}
          <HomepageNDDBanner config={bannerConfig} onCtaClick={handleNDDCTAClick} />
        </>
      ) : null}
    </Box>
  )
}
